import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useWorkspaces } from './useWorkspaces';
import { useToast } from './use-toast';
import { captureError } from '@/lib/sentry';
import type { SortField } from '@/types/contact';

export interface SavedViewCondition {
  id: string;
  table: string;
  field: string;
  operator: string;
  value: string;
  valueEnd?: string;
  // Relative date fields
  dateMode?: 'absolute' | 'relative';
  relativeValue?: number;
  relativeUnit?: 'days';
  relativeDirection?: 'ago' | 'from_now';
  relativeValueEnd?: number;
  relativeDirectionEnd?: 'ago' | 'from_now';
}

export interface SavedView {
  id: string;
  name: string;
  query_criteria: {
    group_type: 'AND' | 'OR';
    groups: Array<{
      id: string;
      type: 'AND' | 'OR';
      conditions: SavedViewCondition[];
    }>;
    sort?: SortField[];
  };
  created_at: string;
  sort_order?: number | null;
}

export function useViews() {
  const [views, setViews] = useState<SavedView[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();

  const fetchViews = async () => {
    if (!currentWorkspace) {
      setViews([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('views')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setViews(data || []);
    } catch (error) {
      console.error('Error fetching views:', error);
      captureError(error as Error, { feature: 'views-fetch' });
      toast({
        title: "Error",
        description: "Failed to load filters",
        variant: "destructive",
      });
      setViews([]);
    } finally {
      setLoading(false);
    }
  };

  const saveView = async (name: string, queryCriteria: any) => {
    if (!currentWorkspace || !user) {
      toast({
        title: "Error",
        description: "No workspace selected",
        variant: "destructive",
      });
      return;
    }

    try {
      // Ensure the query criteria has the proper shape expected by the filter_contacts function
      const formattedCriteria = {
        ...queryCriteria,
        group_type: queryCriteria.group_type || 'AND',
        groups: (queryCriteria.groups || []).map((group: any) => ({
          ...group,
          type: group.type || 'AND',
          conditions: (group.conditions || []).map((condition: any) => ({
            ...condition,
            // Ensure table field exists for all conditions
            table: condition.table || condition.field?.split('.')[0] || 'contacts',
            field: condition.field?.split('.')[1] || condition.field
          }))
        }))
      };

      // Compute next sort_order so new filters append to the bottom.
      const { data: maxRow, error: maxError } = await supabase
        .from('views')
        .select('sort_order')
        .eq('workspace_id', currentWorkspace.id)
        .order('sort_order', { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();

      if (maxError) throw maxError;
      const nextSortOrder = (maxRow?.sort_order ?? 0) + 1;

      const { data, error } = await supabase
        .from('views')
        .insert({
          name,
          query_criteria: formattedCriteria,
          workspace_id: currentWorkspace.id,
          user_id: user.id,
          sort_order: nextSortOrder,
        })
        .select()
        .single();

      if (error) throw error;
      await fetchViews();
      return data;
    } catch (error) {
      console.error('Error saving view:', error);
      captureError(error as Error, { feature: 'view-save' });
      toast({
        title: "Error",
        description: "Failed to save filter",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteView = async (id: string) => {
    try {
      const { error } = await supabase
        .from('views')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchViews();
    } catch (error) {
      console.error('Error deleting view:', error);
      captureError(error as Error, { feature: 'view-delete' });
      toast({
        title: "Error",
        description: "Failed to delete filter",
        variant: "destructive",
      });
      throw error;
    }
  };

  const reorderViews = async (orderedIds: string[]) => {
    try {
      const { error } = await supabase.rpc('reorder_views', {
        view_ids: orderedIds,
      });

      if (error) throw error;
      await fetchViews();
    } catch (error) {
      console.error('Error reordering views:', error);
      captureError(error as Error, { feature: 'views-reorder' });
      toast({
        title: "Error",
        description: "Failed to reorder filters",
        variant: "destructive",
      });
      // Resync to discard the optimistic order on the caller.
      await fetchViews();
      throw error;
    }
  };

  useEffect(() => {
    if (currentWorkspace) {
      fetchViews();
    }
  }, [currentWorkspace?.id]);

  return {
    views,
    loading,
    saveView,
    deleteView,
    reorderViews,
    refetch: fetchViews,
  };
}